import { useFormScrollIntoView } from "@/hooks/use-form-scrollInto-view";
import { FormikProps, useFormik } from "formik";
import { useCallback, useMemo } from "react";
import {
  GROUP_FORM_MODE,
  GROUP_STATUS,
  getGroupValidationSchema,
} from "../group-visit-constant";
import {
  GroupDetail,
  GroupFormMode,
  GroupFormValues,
  GroupPayload,
} from "../group-visit-types";
import { toGroupFormValues, toGroupPayload } from "../group-visit-utils";

export type UseGroupFormProps = {
  mode: GroupFormMode;
  /** Sections on screen; their fields are the ones validated. */
  sections?: string[];
  group?: GroupDetail;
  /** Names of other groups; the duplicate-name check is case-insensitive. */
  existingGroupNames?: string[];
  onSubmit: (payload: GroupPayload) => Promise<unknown> | void;
};

export type UseGroupForm = {
  formik: FormikProps<GroupFormValues>;
  isEditMode: boolean;
  isArchived: boolean;
  enrolledPatientCount: number;
  resetToSaved: () => void;
};

/**
 * @author Rajkumar Rathod
 */
export const useGroupForm = ({
  mode,
  sections,
  group,
  existingGroupNames = [],
  onSubmit,
}: UseGroupFormProps): UseGroupForm => {
  const enrolledPatientCount = group?.enrolledPatientCount ?? 0;

  const initialValues = useMemo(() => toGroupFormValues(group), [group]);

  const validationSchema = useMemo(() => {
    const savedName = group?.groupName?.trim().toLowerCase();

    return getGroupValidationSchema({
      // The group keeps its own name; only other groups make it a duplicate.
      existingGroupNames: existingGroupNames
        .map((name) => name.trim().toLowerCase())
        .filter((name) => name !== savedName),
      enrolledPatientCount,
      // isExistingGroup: !!group,
      sections,
    });
  }, [existingGroupNames, enrolledPatientCount, sections, group]);

  const formik = useFormik<GroupFormValues>({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      try {
        await onSubmit(toGroupPayload(values));
      } finally {
        helpers.setSubmitting(false);
      }
    },
  });

  useFormScrollIntoView(formik);

  const resetToSaved = useCallback(() => {
    formik.resetForm({ values: initialValues });
  }, [formik, initialValues]);

  return {
    formik,
    isEditMode: mode === GROUP_FORM_MODE.EDIT,
    isArchived: formik.values.status === GROUP_STATUS.ARCHIVED,
    enrolledPatientCount,
    resetToSaved,
  };
};
